import { GifsResult } from '@giphy/js-fetch-api'
import { ReactNode, RefObject, useEffect, useState } from 'react'
import { Dimensions, Position, Range } from 'types'
import { ArtboardFilter, ArtboardRef } from '../artboard-gl/types'
import { MAX_PREVIEW_WIDTH } from './constants'
import { EditorProps, UGEContext } from './context'
import { useDuration } from './hooks/use-duration'
import { useMediaInfo } from './hooks/use-media-info'
import { useMediaSprite } from './hooks/use-media-sprite'
import { useMediaThumbnail } from './hooks/use-media-thumbnail'
import { usePreviewDimensions } from './hooks/use-preview-dimensions'
import {
    UGECropType,
    UGEEditor,
    UGEGAEventCallback,
    UGELayer,
    UGELayerActionLabels,
    UGELayerProps,
    ugeEditors,
} from './types'

type Props = {
    apiKey: string
    artboardRef: RefObject<ArtboardRef | null>
    children?: ReactNode
    crop?: Position
    defaultEditor?: UGEEditor
    enableStickers: boolean
    allowedEditors?: UGEEditor[]
    persistArtboardControls: boolean
    showArtboardControls: boolean
    initialDimensions?: Dimensions
    layerActionLabels?: UGELayerActionLabels
    maxPreviewWidth?: number
    source: File | string
    fetchFavoriteStickers?: (offset: number) => Promise<GifsResult>
    setCrop: (crop?: Position) => void
    setDimensions: (dimensions: Dimensions | null) => void
    setGifIds: (ids: string[]) => void
    setStickerLayer: (layer?: UGELayer) => void
    trackGAEvent: UGEGAEventCallback
}

const UGEContextManager = ({
    apiKey,
    artboardRef,
    children,
    crop,
    defaultEditor = 'default',
    enableStickers,
    allowedEditors: allowedEditorsProp,
    persistArtboardControls,
    showArtboardControls,
    initialDimensions,
    layerActionLabels = {},
    maxPreviewWidth = MAX_PREVIEW_WIDTH,
    source,
    fetchFavoriteStickers,
    setCrop,
    setDimensions,
    setGifIds,
    setStickerLayer,
    trackGAEvent,
}: Props) => {
    const allowedEditors = allowedEditorsProp?.length ? allowedEditorsProp : [...ugeEditors]
    const singleStickerMode = allowedEditors.length === 1 && allowedEditors[0] === 'stickers'
    const initialEditor = allowedEditors.includes(defaultEditor) ? defaultEditor : allowedEditors[0] || 'default'
    const [activeEditor, setActiveEditor] = useState<UGEEditor>(initialEditor)
    const [activeEditorProps, setActiveEditorProps] = useState<EditorProps | undefined>(undefined)
    const [cropType, setCropType] = useState<UGECropType>('original')
    const [filter, setFilter] = useState<ArtboardFilter | undefined>()
    const [filterIntensity, setFilterIntensity] = useState<number | undefined>()
    const [layerPositions, setLayerPositions] = useState<Record<string, Position>>({})
    const [layers, setLayers] = useState<UGELayer[]>([])
    const [paused, setPaused] = useState<boolean>(false)
    const [reversed, setReversed] = useState<boolean>(false)
    const [trim, setTrim] = useState<Range>({ start: 0, end: 1 })
    const sourceInfo = useMediaInfo(source)
    const sourceSprite = useMediaSprite(source, sourceInfo, maxPreviewWidth)
    const duration = useDuration(sourceInfo?.duration || 0, layers, !!filter)
    const thumbnail = useMediaThumbnail(sourceSprite)

    const dimensions = usePreviewDimensions(sourceInfo?.width, sourceInfo?.height, maxPreviewWidth, initialDimensions)

    function addLayer(layer: UGELayer) {
        setLayers((layers) =>
            singleStickerMode && (layer.type === 'sticker' || layer.type === 'gif')
                ? [...layers.filter((l) => l.type !== 'sticker' && l.type !== 'gif'), layer]
                : [...layers, layer]
        )
    }

    function getProgress() {
        return artboardRef.current?.getProgress() || 0
    }

    function openEditor(editor: UGEEditor, props?: EditorProps) {
        if (!allowedEditors.includes(editor)) return

        setActiveEditorProps(props)
        setActiveEditor(editor)
    }

    function removeLayerById(id: string) {
        setLayerPositions((positions) => {
            const nextPositions = { ...positions }
            delete nextPositions[id]
            return nextPositions
        })
        setLayers(layers.filter((layer) => layer.id !== id))
    }

    function resetEditorProps() {
        setActiveEditorProps(undefined)
    }

    function scrub(progress: number) {
        artboardRef.current?.setProgress(progress)
    }

    function updateLayerById(id: string, props: Partial<UGELayerProps>) {
        setLayers(
            layers.map((l) => {
                if (l.id === id) {
                    return { ...l, props: { ...l.props, ...props } }
                }

                return l
            })
        )
    }

    function updateLayerPositionById(id: string, position: Position) {
        setLayerPositions((positions) => ({ ...positions, [id]: position }))

        const layer = layers.find((layer) => layer.id === id)

        if (layer?.type === 'sticker' || layer?.type === 'gif') {
            setStickerLayer({ ...layer, props: { ...layer.props, ...position } })
        }
    }

    useEffect(() => {
        setDimensions(dimensions)
    }, [dimensions, setDimensions])

    useEffect(() => {
        const layersWithGifIds = layers.filter((l) => !!l.gifId)
        const gifIds = layersWithGifIds.map((l) => l.gifId || '')

        setGifIds(gifIds)
    }, [layers])

    useEffect(() => {
        const stickerLayer = layers.find((layer) => layer.type === 'sticker' || layer.type === 'gif')

        if (!stickerLayer) {
            setStickerLayer(undefined)
            return
        }

        const position = layerPositions[stickerLayer.id]

        setStickerLayer(position ? { ...stickerLayer, props: { ...stickerLayer.props, ...position } } : stickerLayer)
    }, [layerPositions, layers, setStickerLayer])

    return (
        <UGEContext.Provider
            value={{
                activeEditor,
                activeEditorProps,
                apiKey,
                crop,
                cropType,
                dimensions,
                duration,
                enableStickers,
                allowedEditors,
                persistArtboardControls,
                showArtboardControls,
                filter,
                filterIntensity,
                layerActionLabels,
                layerPositions,
                layers,
                loaded: !!sourceInfo && !!sourceSprite,
                maxPreviewWidth,
                paused,
                reversed,
                source,
                sourceInfo,
                sourceSprite,
                thumbnail,
                trim,
                addLayer,
                fetchFavoriteStickers,
                getProgress,
                openEditor,
                removeLayerById,
                resetEditorProps,
                scrub,
                setCrop,
                setCropType,
                setFilter,
                setFilterIntensity,
                setLayers,
                setPaused,
                setReversed,
                setTrim,
                trackGAEvent,
                updateLayerPositionById,
                updateLayerById,
            }}
        >
            {children}
        </UGEContext.Provider>
    )
}

export default UGEContextManager
